import React, { useEffect, useState, useRef } from "react";
import CheckBox from "./CheckBox";
import ButtonIconV from "./ButtonIconV";
import "../css/FileViewer.css";
import { formatValueUnit, selectedOptic, userData } from "../js/utils";
import { fileSectionsList } from "../js/fileManagement";
import { post } from "../js/post";
import FileDisplay from "./FileDisplay";
import LoadingScreen from "./LoadingScreen";
import DataTable from "react-data-table-component";
import { fileViewerColumns } from "../js/tableColumns";
import { MoveFile } from "./MoveFile";

export default function FileViewer(props) {
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileList, setFileList] = useState([]);
  const [fileData, setFileData] = useState(null);
  const fileInputRef = useRef(null);
  const [loadingFile, setLoadingFile] = useState(false);
  const [animationClass, setAnimationClass] = useState("");
  const [loadingMessage, setLoadingMessage] = useState("");
  const [loadingData, setLoadingData] = useState(true);
  const [selectedSections, setSelectedSections] = useState([]);
  const [selectedSection, setSelectedSection] = useState({ id: "", text: "" });
  const [filesIDList, setFilesIDList] = useState([]);
  const [handleMoveFile, setHandleMoveFile] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const conditionalRowStyleFile = [
    {
      when: (row) => row.idFichero === selectedFile,
      classNames: ["selected-row"],
    },
  ];

  const handleCheck = (e) => {
    setSelectedFile(selectedFile === e ? null : e);
  };

  const getFiles = async () => {
    const response = await post("ListarFicheros.php", {
      token: userData.token,
      optica: selectedOptic.id,
      idPaciente: props.patientId,
      categoria: selectedSections.map((e) => e.id),
    });
    setFileList(response);
    props.reloadFiles();
  };

  const getFile = async () => {
    setLoadingMessage("Cargando...");
    setLoadingFile(true);
    const response = await post("ObtenerFichero.php", {
      token: userData.token,
      idFichero: selectedFile,
    });
    setFileData(response.fichero);
    setLoadingFile(false);
  };

  const uploadFile = async (file, name) => {
    if (props.patientId !== -1) {
      setLoadingFile(true);
      const response = await post("SubirFichero.php", {
        token: userData.token,
        optica: selectedOptic.id,
        idPaciente: props.patientId,
        nombreFichero: name,
        fichero: file,
        categoria: selectedSections[0].id,
        idAgente: userData.id,
      });
      if (response.error === "") {
        await getFiles();
        handleCheck(response.nombreFichero);
      } else {
        props.createPopupQuestion(
          response.error,
          props.removePopupQuestion,
          null,
          "Entendido",
        );
      }
      setLoadingFile(false);
    } else {
      props.createPopupQuestion(
        `Guarde primero la ficha del paciente.`,
        props.removePopupQuestion,
        null,
        "Entendido",
      );
      setSelectedFile(null);
    }
  };

  const deleteFile = async () => {
    await post("EliminarFichero.php", {
      token: userData.token,
      idFichero: selectedFile,
    });
  };

  const moveFile = async (category) => {
    const response = await post("MoverFichero.php", {
      token: userData.token,
      idFichero: selectedFile,
      categoria: category,
    });
    if (response.nombreFichero) {
      setHandleMoveFile(false);
      await getFiles();
      props.createPopupQuestion(
        `El fichero se ha movido a la sección seleccionada.`,
        props.removePopupQuestion,
        null,
        "Entendido",
      );
    }
  };

  const handleNext = () => {
    if (selectedFile) {
      const index = filesIDList.indexOf(selectedFile);
      if (index + 1 < filesIDList.length) {
        setAnimationClass("fade-exit");
        setTimeout(() => {
          setSelectedFile(filesIDList[index + 1]);
          setAnimationClass("fade-enter");
        }, 500);
      }
    }
  };

  const handlePrev = () => {
    if (selectedFile) {
      const index = filesIDList.indexOf(selectedFile);
      if (index - 1 >= 0) {
        setAnimationClass("fade-exit");
        setTimeout(() => {
          setSelectedFile(filesIDList[index - 1]);
          setAnimationClass("fade-enter");
        }, 500);
      }
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file.name);
      convertToBase64(file);
    }
  };

  const convertToBase64 = (file) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onloadend = async () => {
      setLoadingMessage("Subiendo fichero...");
      await uploadFile(reader.result, file.name);
    };
  };

  const handleDownload = () => {
    if (selectedFile) {
      // Crear un enlace temporal
      const link = document.createElement("a");
      link.href = fileData;
      link.download = selectedFile;

      // Añadir el enlace al documento y simular un clic
      document.body.appendChild(link);
      link.click();

      // Eliminar el enlace del documento
      document.body.removeChild(link);
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (selectedSections.length === 0 || selectedSections.length > 1) {
      props.createPopupQuestion(
        `Seleccione solo la sección deseada para subir el fichero.`,
        props.removePopupQuestion,
        null,
        "Entendido",
      );
    } else {
      const file = event.dataTransfer.files[0];
      if (file) {
        setSelectedFile(file.name);
        convertToBase64(file);
      }
    }
  };

  useEffect(() => {
    async function initData() {
      await getFiles();
      setLoadingData(false);
    }
    initData();
    if (selectedSections.length === 0) setSelectedFile(null);
  }, [selectedSections]);

  useEffect(() => {
    if (selectedFile) {
      getFile();
    } else {
      setFileData(null);
    }
  }, [selectedFile]);

  useEffect(() => {
    if (animationClass === "fade-enter") {
      setTimeout(() => {
        setAnimationClass("");
      }, 500);
    }
  }, [animationClass]);

  useEffect(() => {
    setFilesIDList(
      fileList.flatMap((section) => section.ficheros.map((f) => f.idFichero)),
    );
  }, [fileList]);

  useEffect(() => {
    const onDragEnter = () => setIsDragging(true);
    const onDragLeave = (e) => {
      if (!e.relatedTarget) setIsDragging(false);
    };
    const onDrop = () => setIsDragging(false);
    document.addEventListener("dragenter", onDragEnter);
    document.addEventListener("dragleave", onDragLeave);
    document.addEventListener("drop", onDrop);
    return () => {
      document.removeEventListener("dragenter", onDragEnter);
      document.removeEventListener("dragleave", onDragLeave);
      document.removeEventListener("drop", onDrop);
    };
  }, []);

  useEffect(() => {
    const activeIDs = fileList
      .filter((section) =>
        selectedSections.some((s) => s.id === section.familia),
      )
      .flatMap((section) => section.ficheros.map((f) => f.idFichero));

    const setFileAndSection = (idFichero) => {
      setSelectedFile(idFichero);
      if (idFichero) {
        const section = fileList.find((s) =>
          s.ficheros.some((f) => f.idFichero === idFichero),
        );
        const sectionItem = fileSectionsList.find(
          (s) => s.id === section?.familia,
        );
        if (sectionItem) setSelectedSection(sectionItem);
      }
    };

    if (activeIDs.length === 0) {
      setSelectedFile(null);
      setSelectedSection({ id: "", text: "" });
      return;
    }

    if (!selectedFile) {
      if (activeIDs.length > 0) setFileAndSection(activeIDs[0]);
      return;
    }

    if (!activeIDs.includes(selectedFile)) {
      const currentIndex = filesIDList.indexOf(selectedFile);
      const nextID =
        filesIDList
          .slice(currentIndex + 1)
          .find((id) => activeIDs.includes(id)) ??
        filesIDList
          .slice(0, currentIndex)
          .find((id) => activeIDs.includes(id)) ??
        null;
      setFileAndSection(nextID);
    }
  }, [selectedSections, fileList]);

  return (
    <>
      {loadingData && (
        <LoadingScreen message="Cargando ficheros..."></LoadingScreen>
      )}
      {!loadingData && (
        <div
          className="tab-content bg-transparent"
          style={{
            padding: 0,
            paddingTop: 10,
            paddingBottom: 5,
            overflow: "hidden",
          }}
        >
          {handleMoveFile && (
            <MoveFile
              section={selectedSection}
              fileID={selectedFile}
              onAccept={async (category) => {
                await moveFile(category);
              }}
              onClose={() => {
                setHandleMoveFile(false);
              }}
            />
          )}
          <div
            className="content-grid"
            style={{
              height: "100%",
              display: "grid",
              gridTemplateRows: "auto 1fr",
              gap: 10,
            }}
          >
            <div
              className="popup-screen-box container-panel fv-col-sec"
              style={{ gridColumn: "1/span 2", gridRow: "1/span 1" }}
            >
              <span className="section-history-header">Secciones</span>
              <div className="opt-table-tooltip grid-5" id="fv-grid">
                {fileSectionsList
                  .reduce((rows, e, i) => {
                    if (i % 2 === 0) rows.push([e]);
                    else rows[rows.length - 1].push(e);
                    return rows;
                  }, [])
                  .map(([a, b], i) => (
                    <React.Fragment key={i}>
                      <div className="cell">
                        <CheckBox
                          active={selectedSections.includes(a)}
                          onClick={() =>
                            setSelectedSections((prev) =>
                              prev.includes(a)
                                ? prev.filter((s) => s !== a)
                                : [...prev, a],
                            )
                          }
                        />
                      </div>
                      <div
                        className="cell cell-label cell-start-content"
                        style={{ cursor: "pointer" }}
                        onClick={() =>
                          setSelectedSections((prev) =>
                            prev.includes(a)
                              ? prev.filter((s) => s !== a)
                              : [...prev, a],
                          )
                        }
                      >
                        {a.text
                          .toLowerCase()
                          .replace(/^./, (c) => c.toUpperCase())}
                      </div>
                      <div className="cell"></div>
                      {b ? (
                        <>
                          <div className="cell">
                            <CheckBox
                              active={selectedSections.includes(b)}
                              onClick={() =>
                                setSelectedSections((prev) =>
                                  prev.includes(b)
                                    ? prev.filter((s) => s !== b)
                                    : [...prev, b],
                                )
                              }
                            />
                          </div>
                          <div
                            className="cell cell-label cell-start-content"
                            style={{ cursor: "pointer" }}
                            onClick={() =>
                              setSelectedSections((prev) =>
                                prev.includes(b)
                                  ? prev.filter((s) => s !== b)
                                  : [...prev, b],
                              )
                            }
                          >
                            {b.text
                              .toLowerCase()
                              .replace(/^./, (c) => c.toUpperCase())}
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="cell combined-cells-2"></div>
                        </>
                      )}
                    </React.Fragment>
                  ))}
              </div>
            </div>
            <div
              className="popup-screen-box container-panel"
              style={{
                gridColumn: "1/span 2",
                gridRow: "2/span 7",
                overflow: "hidden",
                minHeight: 0,
              }}
            >
              <div
                className="fv-col-sec"
                style={{
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <span className="section-history-header">Explorador</span>
                <ul
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 10,
                    overflowY: "auto",
                    flex: 1,
                    minHeight: 0,
                    width: "100%",
                  }}
                >
                  {fileList.map((e, i) => {
                    return (
                      <div
                        className="thin-rounded-border"
                        key={i}
                        style={{
                          width: "auto",
                          marginRight: 5,
                        }}
                      >
                        <div className="opt-table-title">
                          {fileSectionsList
                            .find((s) => s.id === e.familia)
                            ?.text.toLowerCase()
                            .replace(/^./, (c) => c.toUpperCase()) ??
                            e.familia
                              .toLowerCase()
                              .replace(/^./, (c) => c.toUpperCase())}{" "}
                          ({formatValueUnit(e.ficheros.length)})
                        </div>
                        <DataTable
                          columns={fileViewerColumns}
                          data={e.ficheros}
                          className="custom-datatable datatable-lists"
                          conditionalRowStyles={conditionalRowStyleFile}
                          noDataComponent="No hay ficheros disponibles."
                          onRowClicked={(row, event) => {
                            setSelectedFile(row.idFichero);
                            setSelectedSection(
                              fileSectionsList.find((s) => s.id === e.familia),
                            );
                          }}
                        />
                      </div>
                    );
                  })}
                </ul>
              </div>
            </div>
            <div
              className="popup-screen-box container-panel"
              style={{ gridColumn: "3/span 6", gridRow: "1/span 8" }}
            >
              <div className="file-info-container">
                <div className="file-info-info">
                  <img src="/images/info-blue.svg" alt="info"></img>
                  <span>
                    Para subir un fichero, pulse el botón añadir o arrástrelo
                    hasta el área de visualización.
                  </span>
                </div>
                <input
                  type="file"
                  ref={fileInputRef}
                  style={{ display: "none" }}
                  onChange={handleFileChange}
                />
                <div
                  className="file-info"
                  onDragOver={handleDragOver}
                  onDragEnter={() => setIsDragging(true)}
                  onDragLeave={() => setIsDragging(false)}
                  onDrop={(e) => {
                    setIsDragging(false);
                    handleDrop(e);
                  }}
                  style={{ position: "relative" }}
                >
                  {isDragging && (
                    <div
                      style={{ position: "absolute", inset: 0, zIndex: 10 }}
                      onDragOver={handleDragOver}
                      onDrop={(e) => {
                        setIsDragging(false);
                        handleDrop(e);
                      }}
                    />
                  )}
                  {loadingFile && (
                    <LoadingScreen message={loadingMessage}></LoadingScreen>
                  )}
                  {!loadingFile && fileData && (
                    <FileDisplay base64Data={fileData}></FileDisplay>
                  )}
                </div>
                <div className="file-options-container">
                  {selectedFile && (
                    <ButtonIconV
                      text="Anterior"
                      icon="/images/prev-blue.svg"
                      onClick={handlePrev}
                    ></ButtonIconV>
                  )}
                  <ButtonIconV
                    text="Añadir"
                    icon={
                      props.patientId !== -1
                        ? "/images/create-blue.svg"
                        : "/images/create-gray.svg"
                    }
                    onClick={() => {
                      if (props.patientId !== -1) {
                        if (
                          selectedSections.length > 1 ||
                          selectedSections.length === 0
                        ) {
                          props.createPopupQuestion(
                            `Seleccione solo la sección deseada para subir el fichero.`,
                            props.removePopupQuestion,
                            null,
                            "Entendido",
                          );
                        } else {
                          fileInputRef.current.click();
                        }
                      } else {
                        props.createPopupQuestion(
                          `Guarde primero la ficha del paciente.`,
                          props.removePopupQuestion,
                          null,
                          "Entendido",
                        );
                      }
                    }}
                  ></ButtonIconV>
                  <ButtonIconV
                    text="Mover"
                    icon={
                      selectedFile
                        ? "/images/move-file-blue.svg"
                        : "/images/move-file-gray.svg"
                    }
                    onClick={() => {
                      if (selectedFile) setHandleMoveFile(true);
                    }}
                  ></ButtonIconV>
                  <ButtonIconV
                    text="Eliminar"
                    icon={
                      selectedFile
                        ? "/images/remove-blue.svg"
                        : "/images/remove-gray.svg"
                    }
                    onClick={() => {
                      if (selectedFile) {
                        props.createPopupQuestion(
                          `¿Desea eliminar el fichero?`,
                          async () => {
                            await deleteFile();
                            await getFiles();
                            props.removePopupQuestion();
                            setSelectedFile(null);
                          },
                          props.removePopupQuestion,
                          "Sí",
                          "No",
                        );
                      }
                    }}
                  ></ButtonIconV>
                  <ButtonIconV
                    text="Descargar"
                    icon={
                      selectedFile
                        ? "/images/download-blue.svg"
                        : "/images/download-gray.svg"
                    }
                    onClick={handleDownload}
                  ></ButtonIconV>
                  {selectedFile && (
                    <ButtonIconV
                      text="Siguiente"
                      icon="/images/next-blue.svg"
                      onClick={handleNext}
                    ></ButtonIconV>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
